import { z } from 'zod';

import {
  assertPickedDayCoherent,
  CivilDateDto,
  PickedCivilDayDto,
} from './civilDate';

import { isRealCivilDay, isWireDay } from '../utils/date';
import { ScheduleNoteRecurrenceType } from '../enums';

export const CreateScheduleQuestionnaireDto = z
  .object({
    questionnaireId: z.string().uuid(),
    clientId: z.string().uuid(),
    organizationId: z.string().uuid().optional(),
    dateTime: z.string().datetime(),
    // D1: the civil day the scheduler PICKED (global-timezone spec)
    businessDate: PickedCivilDayDto.optional(),
    deadlineDays: z.number().int().min(1).optional(),
    recurrenceType: z.nativeEnum(ScheduleNoteRecurrenceType).optional(),
    recurrenceInterval: z.number().int().positive().optional(),
    // Both are expanded into one row (plus one submission) per occurrence, so an
    // unparseable value must be rejected here rather than silently degrading to
    // `Invalid Date` and producing zero occurrences with a success response.
    recurrenceEndDate: CivilDateDto.optional(),
    // Dual-mode entries (same convention as the list filters): a bare civil
    // day is the picked day verbatim; ISO instants stay for legacy bundles.
    customDates: z
      .array(
        z
          .string()
          .refine(
            (value) =>
              isRealCivilDay(value) || !Number.isNaN(Date.parse(value)),
            { message: 'VALIDATION__InvalidDate' },
          ),
      )
      .optional(),
  })
  // Same guard as CreateScheduleNoteDto: EVERY_N_DAYS is only a series with
  // an inclusive civil end day.
  .refine(
    (data) =>
      data.recurrenceType !== ScheduleNoteRecurrenceType.EVERY_N_DAYS ||
      data.recurrenceEndDate !== undefined,
    { message: 'VALIDATION__Required', path: ['recurrenceEndDate'] },
  )
  .superRefine((data, ctx) =>
    assertPickedDayCoherent(ctx, data.businessDate, data.dateTime),
  );

export type ApiCreateScheduleQuestionnaireDto = z.infer<
  typeof CreateScheduleQuestionnaireDto
>;

export const UpdateScheduleQuestionnaireDto = z
  .object({
    dateTime: z.string().datetime().optional(),
    // D1: the civil day the scheduler PICKED (global-timezone spec)
    businessDate: PickedCivilDayDto.optional(),
    deadlineDays: z.number().int().min(1).nullable().optional(),
  })
  .superRefine((data, ctx) =>
    assertPickedDayCoherent(ctx, data.businessDate, data.dateTime),
  );

export type ApiUpdateScheduleQuestionnaireDto = z.infer<
  typeof UpdateScheduleQuestionnaireDto
>;

export const ListScheduleQuestionnairesQueryDto = z.object({
  // Optional: omitting it against the organizations/:organizationId mount
  // requests the org-wide (OWNER-only) listing instead of one client's rows.
  clientId: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  // Day-anchored scheduling: a bare `yyyy-MM-dd` pair filters the frozen
  // `businessDate` column; ISO instants keep the legacy dateTime window.
  from: z.string().refine(isWireDay, {
    message: 'VALIDATION__InvalidDayOrDateTime',
  }),
  to: z.string().refine(isWireDay, {
    message: 'VALIDATION__InvalidDayOrDateTime',
  }),
});

export type ApiListScheduleQuestionnairesQueryDto = z.infer<
  typeof ListScheduleQuestionnairesQueryDto
>;
